import React, { useEffect, useState } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import AdminDataTable from "../../../components/common/AdminDataTable";
import { couponListColumns } from "../../../tables/couponListColumns";
import { can } from "../../../utils/can";

export default function AddCoupon() {
  const PAGE_SIZE = 10;
  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";


  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [coupon, setCoupon] = useState([]);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState(null);
  const [success, setSuccess]  = useState();
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [formData, setFormData] = useState({
    name: "",
    couponType: "",
    discount: "",
    startDate: "",
    endDate: "",
    couponDetails: "",
    useLimit: "",
    status : "Deactive"
  });

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // number + percentage validation
    if (name === "discount") {
      if (!/^\d*$/.test(value)) return;
      if (formData.couponType === "Percentage" && Number(value) > 100) return;
    }

    setFormData({ ...formData, [name]: value });
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    let err = {};

    if (!formData.name.trim()) err.name = "Coupon name required";
    if (!formData.couponType) err.couponType = "Coupon type required";
    if (!formData.discount) err.discount = "Discount required";
    if (!formData.startDate) err.startDate = "Start date required";
    if (!formData.endDate) err.endDate = "End date required";
    if (!formData.couponDetails.trim())
      err.couponDetails = "Coupon details required";
    if (!formData.useLimit) err.useLimit = "Use limit required";
    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.endDate) < new Date(formData.startDate)
    ) {
      err.endDate = "End date must be after start date";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    if(can("coupon", "create") === false){
      alert("You don't have permission to create coupons.");
      return;
    }

    e.preventDefault();
    if (!validateForm()) return;

    const url = editId
      ? `${API_URL}/api/admin/coupon-list/update/${editId}`
      : `${API_URL}/api/admin/coupon-create`;

    const method = "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        
        if(data.message === 'Coupon name already exists'){
            setErrors({'name': data.message});
        }
        return;
      }

      resetForm();
      fetchCoupons(page, pageSize);
      setSuccess(data.message);

    } catch (err) {
      console.error(err);
    }
  };

  /* ================= FETCH LIST ================= */
  const fetchCoupons = async (pageNumber = 1, pSize = pageSize) => {
    setListLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/coupon-list?page=${pageNumber}&limit=${pSize}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      setCoupon(data.data || []);
      const computedTotal = data.totalPages
        ? data.totalPages
        : Math.ceil((data.totalCount || data.data.length || 0) / pSize) || 1;
      setTotalPages(computedTotal);
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons(page, pageSize);
  }, [page, pageSize]);

  /* ================= EDIT ================= */
  const handleEdit = (row) => {
    if(can("coupon", "edit") === false){
      alert("You don't have permission to edit coupons.");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditId(row._id);
    setFormData({
      name: row.name,
      couponType: row.couponType,
      discount: row.discount,
      startDate: row.startDate?.substring(0, 10),
      endDate: row.endDate?.substring(0, 10),
      couponDetails: row.couponDetails,
      useLimit: row.useLimit,
      status : row.status
    });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if(can("coupon", "delete") === false){
      alert("You don't have permission to delete coupons.");
      return;
    }
    if (!window.confirm("Delete this coupon?")) return;

    try {
      await fetch(`${API_URL}/api/admin/coupon-list/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCoupons(page, pageSize);
    } catch (err) {
      console.error(err);
    }
  };

  const columns = couponListColumns({
    pageSize,
     handleEdit,
     handleDelete,
  });

  const resetForm = () => {
    setEditId(null);
    setFormData({
      name: "",
      couponType: "",
      discount: "",
      startDate: "",
      endDate: "",
      couponDetails: "",
      useLimit: ""
    });
    setErrors({});
    
  };

  /* ================= JSX ================= */
  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle={editId ? "Edit Coupon" : "Add Coupon"} />

        <div className="right_col" role="main">
          

          <div className="x_panel">
            <div className="x_content">
              <form onSubmit={handleSubmit}>
                <div className="row">

                  <div className="col-sm-3 mb-3">
                    <label>Coupon Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.name && <p className="text-danger">{errors.name}</p>}
                  </div>

                  <div className="col-sm-3 mb-3">
                    <label>Coupon Type</label>
                    <select
                      name="couponType"
                      value={formData.couponType}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select</option>
                      <option value="Discount">Discount</option>
                      <option value="Percentage">Percentage</option>
                    </select>
                    {errors.couponType && <p className="text-danger">{errors.couponType}</p>}
                  </div>

                  <div className="col-sm-3 mb-3">
                    <label>Discount</label>
                    <input
                      type="text"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.discount && <p className="text-danger">{errors.discount}</p>}
                  </div>

                  <div className="col-sm-4 mb-3">
                    <label>Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.startDate && <p className="text-danger">{errors.startDate}</p>}
                  </div>

                  <div className="col-sm-5 mb-3">
                    <label>End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.endDate && <p className="text-danger">{errors.endDate}</p>}
                  </div>

                  <div className="col-sm-9 mb-3">
                    <label>Coupon Details</label>
                    <textarea
                      name="couponDetails"
                      value={formData.couponDetails}
                      onChange={handleChange}
                      className="form-control"
                    />
                    {errors.couponDetails && (
                      <p className="text-danger">{errors.couponDetails}</p>
                    )}
                  </div>
                    <div className="col-md-12"></div>
                  <div className="col-sm-3 mb-3">
                    <label>Use Limit</label>
                    <input type="number" name="useLimit" className="form-control" value={formData.useLimit} onChange={handleChange} /> 
                     {errors.useLimit && (
                      <p className="text-danger">{errors.useLimit}</p>
                    )}
                    </div>

                    <div className="col-sm-3 mb-3">
                      <label>Status </label>
                      <br/>
                      <input type="checkbox" id="active" name="status" value="Active"
                                                            checked={formData.status === "Active"} onChange={handleChange} />
                      <label htmlFor="active" className="form-label">
                          Active/Deactive
                      </label>
                    </div>
                    
                  <div className="col-sm-3 text-right">
                    <button type="submit" className="btn sitebtn">
                      {editId ? "Update" : "Submit"}
                    </button>
                   {success && (
                      <p className="text-success">{success}</p>
                    )}

                  </div>

                </div>
              </form>
            </div>
          </div>

          <div className="x_panel">
            <AdminDataTable
              columns={columns}
              data={coupon}
              page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={listLoading}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
            />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
